import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  ViewChild,
  output,
  input,
} from '@angular/core';

import { timer } from 'rxjs';

import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { IconComponent } from '../../common/icon/icon.component';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import { RtTextInput } from '../../utils/rt-input';
import { ComponentVariantTransform, type Variants } from '../../utils/variant/variant';

@Component({
  selector: 'rt-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.less'],
  host: {
    '[class.disable-invalid-styles]': 'disableInvalidStyles()',
    '[class.disabled]': 'disabled',
    '[class.invalid]': 'invalid()',
    '[class.has-clear-button]': 'hasClearButton()',
    '[style.width]': 'width()',
    '[style.max-width]': 'maxWidth() ? maxWidth() : null',
    '[class.readonly]': 'readOnly()',
    '[class.has-dropdown]': 'hasDropdownIndicator()',
    '[class]': 'variant()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => InputComponent),
    },
  ],
  imports: [SpinnerComponent, IconComponent, IconButtonComponent],
})
export class InputComponent extends RtTextInput {
  public static readonly VARIANTS: Variants = ['rounded', ['rounded', 'plain']];

  public readonly variant = input(...ComponentVariantTransform(InputComponent.VARIANTS));
  public readonly type = input<string>('text');
  public readonly placeholder = input<string>(undefined!);
  public readonly maxLength = input<number>(undefined!);
  public readonly tabIndex = input<number>();
  public readonly width = input<string>(undefined!);
  public readonly maxWidth = input<string | null>(null);
  public readonly autoFocus = input<boolean>(false);
  public readonly hasDropdownIndicator = input<boolean>(false);
  public readonly invalid = input<boolean>(undefined!);
  public readonly readOnly = input<boolean>(false);
  public readonly hasClearButton = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly focusOnClear = input<boolean>(true);
  public readonly displayedValue = input<(string | null) | undefined>(null);
  public readonly autocompleteName = input<string>();
  public readonly disableInvalidStyles = input<boolean>(false);
  public readonly asNumber = input<boolean>(false);
  public readonly preventAutocompleteSuggestions = input<boolean>(false);

  public reset = output<Event>();

  @ViewChild('clearButton', { static: false, read: ElementRef }) public clearButton: ElementRef;
  @ViewChild('input', { static: true }) public input: ElementRef<HTMLInputElement>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value || changes.displayedValue) {
      this.setValue(this.value);
    }

    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }

    if (changes.autoFocus) {
      if (this.autoFocus()) {
        this.setFocus();
      }
    }
  }

  public ngOnInit(): void {
    if (this.autoFocus()) {
      this.setFocus();
    }
  }

  public getNativeInputElement(): HTMLInputElement {
    return this.input.nativeElement;
  }

  public setFocus(): void {
    if (this.input.nativeElement) {
      timer(0).subscribe(() => {
        this.input.nativeElement.focus();
      });
    }
  }

  public setBlur(): void {
    if (this.input.nativeElement) {
      this.input.nativeElement.blur();
    }
  }

  public setInputFocus(): void {
    if (this.input && this.focusOnClear()) {
      this.setFocus();
    }
  }

  public isRemoveButtonVisible(): boolean {
    return !!this.value && this.hasClearButton() && !this.loading();
  }

  public onRemoveButtonClick(event: Event): void {
    this.valueChange.emit('');
    this.reset.emit(event);
    this.setInputFocus();
  }

  public handleChange(value: string): void {
    const parsedValue = <string>(this.asNumber() ? this.parseNumber(value) : value);
    super.handleChange(parsedValue);
  }

  public setValue(value: string | null): void {
    this.value = value;
    this.input.nativeElement.value = this.displayedValue() ?? value ?? '';
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
  }

  private parseNumber(value: string) {
    return Math.min(Math.max(Number.MIN_SAFE_INTEGER, Number(value)), Number.MAX_SAFE_INTEGER);
  }
}
